import { AppliedProfile, ConstraintDocument, ConstraintMode } from 'types/ConstraintProfileTypes';
import { ProductDisplayData } from 'types/ProductTypes';
import { ProductListResponseItem } from 'types/ProductListTypes';
import { diffDocumentPaths } from 'utils/constraintDocument';
import { splitPath } from 'utils/constraintSchema';
import {
  panelSectionForGroup,
  visibleConstraintSectionTypes,
} from 'pages/assistant/components/activity-panel/panels/constraints/panelSections';
import { esgPreferenceMapEngToIta, riskProfileMapEngToIta } from 'utils/misc';
import { fieldLabel, fieldOrder, sectionLabel } from './vincoliRecapLabels';
import {
  formatPercent,
  formatPercentRaw,
  formatNumber,
  formatBoolean,
  formatCurrency,
  formatProductLabel,
} from './vincoliRecapFormat';

export type RecapBullet = {
  label: string;
  value: string;
  /**
   * Optional nested bullets. Rendered one indent level deeper than the parent
   * with the same `• ` marker. Use for "header bullets" that group child
   * items (e.g. `Prodotti individuali` with one child per ISIN). Bullets with
   * children typically have an empty `value`; the renderer drops the
   * trailing `: ` when value is empty so the header reads cleanly.
   */
  children?: RecapBullet[];
};

export type RecapSection = {
  heading: string;
  bullets: RecapBullet[];
  subSections?: RecapSection[];
};

export type Recap = {
  configurationName: string;
  totalBulletCount: number;
  sections: RecapSection[];
};

export type RecapDeps = {
  t: (key: string, params?: Record<string, unknown>) => string;
  getProductDisplay: (isin: string) => ProductDisplayData | undefined;
  productLists: ProductListResponseItem[];
  /**
   * Microcategory id → display name. Populated by
   * `activityPanelStore.fetchMicrocategoryNames` on activity-panel mount.
   * Empty during the first render or when the fetch is in flight; in that
   * case the recap falls back to the raw id, same as the panel renders
   * "caricamento...".
   */
  microcategoryNames: Record<string, string>;
  importedPortfolioProducts: Record<string, { hold_mode?: string | null }> | null;
};

/**
 * Per-path scalar formatter overrides. Anything not in the table falls through
 * to the default rule (boolean → Sì/No, number → formatPercent, string → as-is).
 *
 * The table is keyed by the full `group.field` path. Adding a new SAVABLE_PATHS
 * field to a non-percent type means adding ONE row here — explicit, audit-able,
 * no framework lookup.
 */
const SCALAR_FORMAT_OVERRIDES: Record<
  string,
  (value: unknown, t: (key: string) => string) => string
> = (() => {
  const asInt = (v: unknown) => String(v);
  const asBoolean = (v: unknown, t: (key: string) => string) =>
    formatBoolean(Boolean(v), t);
  // Floats already in percent units (12.6 means 12.6%) — no multiply by 100.
  const asPercentRaw = (v: unknown) =>
    typeof v === 'number' ? formatPercentRaw(v) : String(v ?? '');
  // Plain Italian-formatted number for scores/durations/multipliers.
  const asItalianNumber = (v: unknown) =>
    typeof v === 'number' ? formatNumber(v) : String(v ?? '');

  return {
    // Integer counts
    'risk.financial_plan': asInt,
    'asset.asset_count_min': asInt,
    'asset.asset_count_max': asInt,
    'order.assets_to_buy_min': asInt,
    'order.assets_to_buy_max': asInt,
    'order.assets_to_sell_min': asInt,
    'order.assets_to_sell_max': asInt,
    'order.positive_amount_min': asInt,

    // Enum strings — translated to Italian via the existing mappers used by
    // the panel components (RiskContent / EsgContent).
    'risk.risk_profile': (v) => riskProfileMapEngToIta(String(v ?? '')),
    'esg.esg_preference': (v) => esgPreferenceMapEngToIta(String(v ?? '')),

    // Booleans → Sì / No
    'risk.align_to_plan': asBoolean,
    'esg.lettera_a': asBoolean,
    'esg.lettera_b': asBoolean,
    'esg.lettera_c': asBoolean,
    'esg.pai_e': asBoolean,
    'esg.pai_s': asBoolean,
    'esg.pai_g': asBoolean,

    // VaR fields are stored in their natural percent scale (12.6 → "12,6%"),
    // not 0..1. No multiply.
    'risk.target_var': asPercentRaw,
    'risk.target_var_min': asPercentRaw,
    'risk.target_var_max': asPercentRaw,

    // Float, no unit (scores 1..10, durations in years, threshold multipliers).
    'indicator.final_score_min': asItalianNumber,
    'indicator.final_score_advice_min': asItalianNumber,
    'indicator.esg_rating_min': asItalianNumber,
    'dividend.duration_min': asItalianNumber,
    'dividend.duration_max': asItalianNumber,
    'investment_threshold.investment_bonds_max': asItalianNumber,
    'investment_threshold.investment_funds_max': asItalianNumber,
  };
})();

/**
 * Format a scalar field's value for the recap. The path is `group.field`;
 * if it appears in `SCALAR_FORMAT_OVERRIDES`, use that. Otherwise:
 *   - boolean → Sì/No via t()
 *   - number  → formatPercent (the SAVABLE_PATHS default; 60+ fields are 0..1)
 *   - null/undefined → ''
 *   - string → as-is
 */
function formatScalarValue(
  path: string,
  value: unknown,
  t: (key: string) => string,
): string {
  const override = SCALAR_FORMAT_OVERRIDES[path];
  if (override) return override(value, t);
  if (typeof value === 'boolean') return formatBoolean(value, t);
  if (typeof value === 'number') return formatPercent(value);
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * Microcategory bullets render as `<name>: <value>`. One-sided ranges get a
 * "(min)" / "(max)" suffix on the LABEL — the same convention the panel-
 * section bullets use ("Obbligazionario Internazionale (max): 5,00%") — so a
 * reader sees one consistent indicator across the whole recap.
 */
function formatMicrocategoryBullet(
  name: string,
  w: { min?: number; max?: number },
): { label: string; value: string } {
  const hasMin = typeof w.min === 'number';
  const hasMax = typeof w.max === 'number';
  if (hasMin && hasMax) {
    return { label: name, value: `${formatPercent(w.min!)} – ${formatPercent(w.max!)}` };
  }
  if (hasMin) return { label: `${name} (min)`, value: formatPercent(w.min!) };
  if (hasMax) return { label: `${name} (max)`, value: formatPercent(w.max!) };
  return { label: name, value: '–' };
}

type IndividualProductFields = {
  weight_min?: number | null;
  weight_max?: number | null;
  value_min?: number | null;
  value_max?: number | null;
};

function hasAnyExplicitField(f: IndividualProductFields): boolean {
  return (
    f.weight_min != null || f.weight_max != null
    || f.value_min != null || f.value_max != null
  );
}

/**
 * Italian label for a hold_mode value. Mirrors the modal column "Blocco
 * Posizione" — the long pane tooltips ("Nessun blocco - vincoli modificabili")
 * carry too much noise for an inline recap, so we keep just the title.
 */
function formatHoldMode(mode: string): string {
  switch (mode) {
    case 'none': return 'Nessun blocco';
    case 'hold': return 'Mantieni posizione';
    case 'hold_or_sell': return 'Mantieni o vendi';
    default: return mode;
  }
}

/**
 * Format a Portafoglio cliente row as a single inline value. The modal only
 * exposes Controvalore (Min/Max) and Blocco Posizione for portfolio holdings
 * — Peso PTF is read-only — so weight inputs are intentionally ignored.
 * Field labels are written inline without inner colons so the row sits on
 * one line under the product name without a doubled `:`.
 */
function formatPortfolioRowSummary(
  f: { value_min?: number | null; value_max?: number | null;
       hold_mode?: string | null },
  importedHoldMode: string | null,
): string {
  const parts: string[] = [];
  const vMin = f.value_min != null;
  const vMax = f.value_max != null;
  if (vMin && vMax) {
    parts.push(`Controvalore ${formatCurrency(f.value_min!)} – ${formatCurrency(f.value_max!)}`);
  } else if (vMin) {
    parts.push(`Controvalore (min) ${formatCurrency(f.value_min!)}`);
  } else if (vMax) {
    parts.push(`Controvalore (max) ${formatCurrency(f.value_max!)}`);
  }
  if ((f.hold_mode ?? null) !== importedHoldMode && f.hold_mode != null) {
    // Drop the "Blocco Posizione" label — the translated hold-mode value
    // ("Mantieni posizione", "Nessun blocco", …) is self-describing and the
    // recap reads cleaner without the redundant header.
    parts.push(formatHoldMode(f.hold_mode));
  }
  return parts.join(', ');
}

/**
 * Format a per-product weight / value range. Two-sided ranges render with a
 * dash ("peso: 1,00% – 2,00%"). One-sided ranges use the same "(min)" /
 * "(max)" suffix style as the rest of the recap ("peso (min): 1,00%"), so the
 * reader sees one consistent convention across sections.
 *
 * Builder mode always uses weights; rebalancing additionally exposes value
 * (controvalore in €).
 */
function formatProductFieldSummary(
  f: IndividualProductFields,
  mode: ConstraintMode,
): string {
  const parts: string[] = [];
  const minSet = f.weight_min != null;
  const maxSet = f.weight_max != null;
  if (minSet && maxSet) {
    parts.push(`peso: ${formatPercent(f.weight_min!)} – ${formatPercent(f.weight_max!)}`);
  } else if (minSet) {
    parts.push(`peso (min): ${formatPercent(f.weight_min!)}`);
  } else if (maxSet) {
    parts.push(`peso (max): ${formatPercent(f.weight_max!)}`);
  }
  if (mode === 'rebalancing') {
    const vMinSet = f.value_min != null;
    const vMaxSet = f.value_max != null;
    if (vMinSet && vMaxSet) {
      parts.push(`valore: ${formatCurrency(f.value_min!)} – ${formatCurrency(f.value_max!)}`);
    } else if (vMinSet) {
      parts.push(`valore (min): ${formatCurrency(f.value_min!)}`);
    } else if (vMaxSet) {
      parts.push(`valore (max): ${formatCurrency(f.value_max!)}`);
    }
  }
  return parts.join(', ');
}

export function buildVincoliRecap(
  working: ConstraintDocument,
  baseline: ConstraintDocument,
  mode: ConstraintMode,
  appliedProfile: AppliedProfile | null,
  deps: RecapDeps,
): Recap {
  const configurationName = appliedProfile?.name
    ?? deps.t('constraints.predefinedConfigName');

  const diffPaths = diffDocumentPaths(working, baseline, mode);
  const bySection: Record<string, RecapBullet[]> = {};
  const productSubSectionsByPanelKey: Record<string, RecapSection[]> = {};
  // Pair `<base>_min` and `<base>_max` deviations into a single "min – max"
  // bullet. The panel shows the two as side-by-side inputs sharing one label,
  // and emitting them separately produced two visually-identical "Costo
  // strumenti: …" rows. Single-side deviations (only min changed, only max
  // changed) fall through to single bullets with a "(min)" / "(max)" suffix
  // so the reader can still tell which side moved.
  // Sort paths so bullets within each panel section appear in the same order
  // as the pane (which iterates `constraints-config.json`'s `data` block in
  // JSON declaration order). `fieldOrder` resolves a field's position within
  // the panel section's data block; unknown fields drift to the end without
  // destabilising the sort. Cross-section order is handled by the outer
  // `orderedSectionKeys` loop further down — here we just keep same-section
  // paths grouped + ordered.
  const sortedDiffPaths = [...diffPaths].sort((a, b) => {
    const [groupA, fieldA] = splitPath(a);
    const [groupB, fieldB] = splitPath(b);
    const sectionA = panelSectionForGroup(groupA);
    const sectionB = panelSectionForGroup(groupB);
    if (sectionA !== sectionB) return sectionA.localeCompare(sectionB);
    return fieldOrder(sectionA, fieldA) - fieldOrder(sectionB, fieldB);
  });

  const consumed = new Set<string>();
  for (const path of sortedDiffPaths) {
    if (consumed.has(path)) continue;
    const [group, field] = splitPath(path);
    const panelSection = panelSectionForGroup(group);
    if (!bySection[panelSection]) bySection[panelSection] = [];

    const value = (working[group] as Record<string, unknown> | null | undefined)?.[field];
    // Labels live in constraints-config.json keyed by PANEL SECTION (e.g.
    // `asset` covers `mutual_funds_*`, `etf_*`, … whose real Document group
    // is `instrument_type`). Use panelSection so the lookup hits.
    const baseLabel = fieldLabel(panelSection, field, deps.t);

    // Min/max pairing.
    if (field.endsWith('_min') || field.endsWith('_max')) {
      const suffix = field.endsWith('_min') ? '_min' : '_max';
      const otherSuffix = suffix === '_min' ? '_max' : '_min';
      const otherField = field.slice(0, -suffix.length) + otherSuffix;
      const otherPath = `${group}.${otherField}`;
      if (diffPaths.includes(otherPath)) {
        const otherValue = (working[group] as Record<string, unknown> | null | undefined)?.[otherField];
        // Format both values via the same override (or fallback), so the
        // unit/locale matches on both sides of the dash.
        const minPath = suffix === '_min' ? path : otherPath;
        const maxPath = suffix === '_min' ? otherPath : path;
        const minValue = suffix === '_min' ? value : otherValue;
        const maxValue = suffix === '_min' ? otherValue : value;
        bySection[panelSection].push({
          label: baseLabel,
          value: `${formatScalarValue(minPath, minValue, deps.t)} – ${formatScalarValue(maxPath, maxValue, deps.t)}`,
        });
        consumed.add(path);
        consumed.add(otherPath);
        continue;
      }
      // Only one side deviates — suffix the label so the reader knows which.
      bySection[panelSection].push({
        label: `${baseLabel} (${suffix === '_min' ? 'min' : 'max'})`,
        value: formatScalarValue(path, value, deps.t),
      });
      consumed.add(path);
      continue;
    }

    bySection[panelSection].push({
      label: baseLabel,
      value: formatScalarValue(path, value, deps.t),
    });
    consumed.add(path);
  }

  // Microcategories: per-entry diff vs baseline. Replace any scalar-loop
  // emission for microcategoria_advice with the per-entry bullets.
  const workingMc =
    ((working.microcategoria_advice as Record<string, unknown> | null | undefined)
      ?.microcategory_weights as Record<string, { min?: number; max?: number }> | undefined)
    ?? {};
  const baselineMc =
    ((baseline.microcategoria_advice as Record<string, unknown> | null | undefined)
      ?.microcategory_weights as Record<string, { min?: number; max?: number }> | undefined)
    ?? {};

  // Resolve microcategory ids to display names via the eagerly-fetched map;
  // fall back to the raw id when the map hasn't loaded yet (matches the
  // pane's "caricamento..." fallback).
  const microcategoryLabel = (id: string) => deps.microcategoryNames[id] ?? id;

  const mcBullets: RecapBullet[] = [];
  const seen = new Set<string>();
  for (const [id, w] of Object.entries(workingMc)) {
    seen.add(id);
    const b = baselineMc[id];
    const changed = !b
      || (b.min ?? null) !== (w.min ?? null)
      || (b.max ?? null) !== (w.max ?? null);
    if (changed) {
      mcBullets.push(formatMicrocategoryBullet(microcategoryLabel(id), w));
    }
  }
  for (const id of Object.keys(baselineMc)) {
    if (seen.has(id)) continue;
    mcBullets.push({ label: microcategoryLabel(id), value: 'rimosso' });
  }
  if (mcBullets.length > 0) {
    bySection['microcategoria_advice'] = mcBullets;
  } else {
    delete bySection['microcategoria_advice'];
  }

  // Liquidity (rebalancing only). Replace any scalar-loop emission with
  // a labeled bullet, or remove the section entirely when amount is 0.
  if (mode === 'rebalancing') {
    const liq = working.liquidity as { amount?: number; withdraw?: boolean } | null | undefined;
    const amount = liq?.amount ?? 0;
    if (amount !== 0) {
      // Mirror the pane dropdown labels (Apporto / Prelievo) and treat the
      // amount as a raw EUR figure — the input is a currency field, not a
      // percentage of portfolio.
      const label = liq?.withdraw ? 'Prelievo' : 'Apporto';
      bySection['liquidity'] = [{ label, value: formatCurrency(amount) }];
    } else {
      delete bySection['liquidity'];
    }
  } else {
    delete bySection['liquidity'];
  }

  // Prodotti: panel section "product" rendered as top-level bullets in a
  // fixed order. Rebalancing mode leads with "Portafoglio cliente" (mirroring
  // the modal heading) when any of the imported holdings have been touched;
  // builder mode shows only the configuration bullets.
  const productGroup = working.product as Record<string, unknown> | null | undefined;
  const productTopBullets: RecapBullet[] = [];

  const individuals = (productGroup?.individual_products
    ?? {}) as Record<string, IndividualProductFields>;
  const selectedListIds = (productGroup?.selected_list_ids ?? []) as string[];
  const listProducts = (productGroup?.list_products
    ?? {}) as Record<string, IndividualProductFields>;

  // 1) Portafoglio cliente — rebalancing only, first when present. An entry
  //    is "untouched" when ALL weight/value inputs are empty AND its
  //    hold_mode matches the imported snapshot. We render the bullet only
  //    when at least one entry is touched, so a default-state portfolio
  //    (advisor didn't open the modal) stays out of the recap entirely.
  if (mode === 'rebalancing') {
    type PortfolioFields = IndividualProductFields & { hold_mode?: string | null };
    const portfolio = (productGroup?.portfolio_products
      ?? {}) as Record<string, PortfolioFields>;
    const portfolioChildren: RecapBullet[] = [];
    for (const [isin, fields] of Object.entries(portfolio)) {
      const display = deps.getProductDisplay(isin);
      // ProductDisplayData carries `default_hold_mode` — the imported value
      // per ISIN — so we compare the working hold_mode against that. The
      // legacy `importedPortfolioProducts` key on the thread metadata is
      // never populated in practice; default_hold_mode is the authoritative
      // signal. When display data hasn't loaded yet we conservatively skip
      // hold_mode tracking for that ISIN to avoid a flash of false changes.
      const defaultHoldMode = display?.default_hold_mode ?? null;
      // The modal only edits Controvalore (Min/Max) and Blocco Posizione for
      // portfolio holdings — Peso PTF is read-only — so a touched entry is
      // one that set a value field or moved hold_mode off its default.
      const hasAnySetValue = fields.value_min != null || fields.value_max != null;
      const holdModeChanged = defaultHoldMode != null
        && fields.hold_mode != null
        && fields.hold_mode !== defaultHoldMode;
      if (!hasAnySetValue && !holdModeChanged) continue;
      // Split into a name line and an indented detail line — long fund
      // names + value range + hold mode would otherwise crowd onto one row
      // and the eye can't tell them apart at a glance.
      portfolioChildren.push({
        label: formatProductLabel(isin, display),
        value: '',
        children: [{
          label: formatPortfolioRowSummary(fields, defaultHoldMode),
          value: '',
        }],
      });
    }
    if (portfolioChildren.length > 0) {
      productTopBullets.push({
        label: 'Portafoglio cliente',
        value: '',
        children: portfolioChildren,
      });
    }
  }

  // 2) Prodotti individuali — every advisor-picked ISIN appears, with its
  //    weight (and value, in rebalancing) range. ISINs without explicit
  //    constraints still appear so the recap records that the advisor
  //    preferred them (just without a value field).
  const individualChildren: RecapBullet[] = [];
  for (const [isin, fields] of Object.entries(individuals)) {
    const display = deps.getProductDisplay(isin);
    const summary = formatProductFieldSummary(fields, mode);
    individualChildren.push({
      label: formatProductLabel(isin, display),
      value: '',
      // Split into a name line and an indented detail line, matching the
      // Portafoglio cliente layout. When no explicit fields are set (the
      // advisor only preferred the ISIN) the name stands alone with no
      // empty detail child to render.
      children: summary ? [{ label: summary, value: '' }] : undefined,
    });
  }
  if (individualChildren.length > 0) {
    productTopBullets.push({
      label: 'Prodotti individuali',
      value: '',
      children: individualChildren,
    });
  }

  // 3) Liste selezionate — one child per selected list. Show list size and
  //    how many of its products carry explicit constraints, reusing the
  //    "Prodotti totali" / "con vincoli" wording from the pane summary so
  //    advisor and agent read the same vocabulary.
  if (selectedListIds.length > 0) {
    const listChildren: RecapBullet[] = [];
    for (const listId of selectedListIds) {
      const list = deps.productLists.find((l) => l.id === listId);
      const heading = list?.name ?? listId;
      const total = list?.products.length ?? 0;
      let constrained = 0;
      for (const [isin, fields] of Object.entries(listProducts)) {
        if (!hasAnyExplicitField(fields)) continue;
        if (list && !list.products.some((p) => p.isin === isin)) continue;
        constrained += 1;
      }
      const totalWord = total === 1 ? 'prodotto' : 'prodotti';
      const value = `${total} ${totalWord}, ${constrained} con vincoli`;
      listChildren.push({ label: heading, value });
    }
    productTopBullets.push({
      label: 'Liste selezionate',
      value: '',
      children: listChildren,
    });
  }

  // 4) Usa solo prodotti preferiti — last, builder-only, gated on having at
  //    least one individual product OR one selected list (otherwise the flag
  //    has nothing to act on and surfacing it is noise).
  if (
    mode === 'builder'
    && (Object.keys(individuals).length > 0 || selectedListIds.length > 0)
  ) {
    const olp = productGroup?.only_list_products === true;
    productTopBullets.push({
      label: 'Usa solo prodotti preferiti',
      value: olp ? deps.t('constraints.yes') : deps.t('constraints.no'),
    });
  }

  if (productTopBullets.length > 0) {
    bySection['product'] = productTopBullets;
  } else {
    delete bySection['product'];
  }
  delete productSubSectionsByPanelKey['product'];

  const orderedSectionKeys = visibleConstraintSectionTypes(mode);
  const sections: RecapSection[] = [];
  let total = 0;
  for (const key of orderedSectionKeys) {
    const bullets = bySection[key] ?? [];
    const subSections = productSubSectionsByPanelKey[key];
    const hasSubSections = (subSections?.length ?? 0) > 0;
    if (bullets.length === 0 && !hasSubSections) continue;
    const section: RecapSection = { heading: sectionLabel(key), bullets };
    if (subSections && subSections.length > 0) {
      section.subSections = subSections;
      total += subSections.reduce((acc, s) => acc + s.bullets.length, 0);
    }
    total += bullets.length;
    sections.push(section);
  }

  return { configurationName, totalBulletCount: total, sections };
}
